/**
 * Score Utilities
 * Unified score tier classification and color mapping for Interview Simulation module
 */

/** Score tier type - based on 10-point intervals */
export type ScoreTier = 'exceptional' | 'excellent' | 'good' | 'acceptable' | 'needsWork' | 'critical';

/**
 * Get score tier based on score value
 * @param score - Score value (0-100)
 * @returns Score tier classification
 *
 * Tier mapping:
 * - 90-100: exceptional (purple)
 * - 80-89: excellent (blue)
 * - 70-79: good (green)
 * - 60-69: acceptable (yellow)
 * - 50-59: needsWork (orange)
 * - 0-49: critical (red)
 */
export const getScoreTier = (score: number): ScoreTier => {
  if (score >= 90) return 'exceptional';
  if (score >= 80) return 'excellent';
  if (score >= 70) return 'good';
  if (score >= 60) return 'acceptable';
  if (score >= 50) return 'needsWork';
  return 'critical';
};

/**
 * Get color class name based on score
 * @param score - Score value
 * @param styles - SCSS module object containing color classes
 * @returns Color class name string
 *
 * Required SCSS classes:
 * - .colorPurple: var(--purple-100)
 * - .colorBlue: var(--ranking-blue)
 * - .colorGreen: var(--green-100)
 * - .colorYellow: var(--yellow-100)
 * - .colorOrange: var(--ranking-orange)
 * - .colorRed: var(--red-100)
 */
export const getScoreColorClass = (score: number, styles: Record<string, string>): string => {
  const tier = getScoreTier(score);
  const colorMap: Record<ScoreTier, string> = {
    exceptional: styles.colorPurple || '',
    excellent: styles.colorBlue || '',
    good: styles.colorGreen || '',
    acceptable: styles.colorYellow || '',
    needsWork: styles.colorOrange || '',
    critical: styles.colorRed || '',
  };
  return colorMap[tier];
};

/**
 * Get ranking text based on score
 * @param score - Score value
 * @returns Ranking percentile text (e.g., "Top 5%")
 */
export const getRankingText = (score: number): string => {
  if (score >= 90) return 'Top 5%';
  if (score >= 80) return 'Top 10%';
  if (score >= 70) return 'Top 20%';
  if (score >= 60) return 'Top 30%';
  if (score >= 50) return 'Top 50%';
  return 'Top 70%';
};
